/**
 * Contact.tsx — Public contact page.
 *
 * Route: /contact — accessible to unauthenticated and authenticated users.
 * Hero, 3 contact option cards, contact form with subject dropdown, office info.
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SecurityIcon from '@mui/icons-material/Security';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import axios from 'axios';
import API_BASE from '../config/api';

// ── Contact option card ───────────────────────────────────────────────────────

function ContactCard({
  icon,
  title,
  description,
  detail,
  cta,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  detail: string;
  cta: string;
  onClick?: () => void;
}) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(0,180,216,0.15)',
        height: '100%',
        transition: 'border-color 0.2s, transform 0.2s',
        '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
      }}
    >
      <CardContent sx={{ p: 3.5, textAlign: 'center', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ color: 'primary.main', mb: 2 }}>{icon}</Box>
        <Typography variant="h6" fontWeight={700} mb={1}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5} sx={{ flex: 1 }}>{description}</Typography>
        <Typography variant="body2" color="primary.main" fontWeight={600} mb={2.5}>{detail}</Typography>
        <Button variant="outlined" size="small" onClick={onClick} sx={{ fontWeight: 600, alignSelf: 'center' }}>
          {cta}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Subject options ───────────────────────────────────────────────────────────

const SUBJECTS = [
  'General enquiry',
  'Security question',
  'Sales & pricing',
  'Technical support',
  'Partnership',
  'Press & media',
  'Other',
];

// ── Main component ────────────────────────────────────────────────────────────

export default function Contact() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [form, setForm] = useState({
    name: '', email: '', company: '', subject: SUBJECTS[0], message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [error, setError]           = useState('');

  function handleChange(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in your name, email, and message.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/contact`, {
        name:    form.name.trim(),
        email:   form.email.trim(),
        company: form.company.trim() || undefined,
        subject: form.subject,
        message: form.message.trim(),
      });
      setSubmitted(true);
    } catch {
      setError('Failed to send your message. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>

      {/* ── Navbar ─────────────────────────────────────────────────────── */}
      <Box
        component="nav"
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: { xs: 2, sm: 5 }, py: 2,
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          position: 'sticky', top: 0, zIndex: 10,
          bgcolor: 'rgba(13,17,23,0.92)', backdropFilter: 'blur(12px)',
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <SecurityIcon sx={{ color: '#00E5FF', fontSize: 26, filter: 'drop-shadow(0 0 6px rgba(0,229,255,0.6))' }} />
          <Typography variant="h6" fontWeight={900} sx={{ color: '#00E5FF', textShadow: '0 0 12px rgba(0,229,255,0.4)' }}>Shield</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {user ? (
            <Button variant="contained" component={Link} to="/dashboard" sx={{ color: '#FFFFFF' }}>
              Go to Dashboard
            </Button>
          ) : (
            <>
              <Button variant="outlined" component={Link} to="/login" sx={{ color: '#FFFFFF', borderColor: '#FFFFFF', '&:hover': { borderColor: '#FFFFFF', bgcolor: 'rgba(255,255,255,0.08)' } }}>
                Sign In
              </Button>
              <Button variant="contained" component={Link} to="/signup" sx={{ fontWeight: 700 }}>
                Start Free Trial
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <Box sx={{ textAlign: 'center', pt: { xs: 6, md: 9 }, pb: 6, px: 3 }}>
        <Typography variant="h3" fontWeight={800} mb={2}>
          Get in touch
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 520, mx: 'auto', fontWeight: 400, lineHeight: 1.6 }}>
          Questions about pricing, security, or integrations? We're here to help — reach us any way that works for you.
        </Typography>
      </Box>

      {/* ── 3 contact cards ────────────────────────────────────────────── */}
      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 8, maxWidth: 960, mx: 'auto' }}>
        <Grid container spacing={3} mb={8}>
          <Grid item xs={12} md={4}>
            <ContactCard
              icon={<SupportAgentIcon sx={{ fontSize: 44 }} />}
              title="General Support"
              description="Questions about your account, features, or how Shield works."
              detail="support@[CONTACT EMAIL]"
              cta="Send email"
              onClick={() => window.open('mailto:support@[CONTACT EMAIL]')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ContactCard
              icon={<EmailIcon sx={{ fontSize: 44 }} />}
              title="Security Team"
              description="Report a vulnerability, security concern, or request a penetration test report."
              detail="security@[CONTACT EMAIL]"
              cta="Contact security"
              onClick={() => window.open('mailto:security@[CONTACT EMAIL]')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ContactCard
              icon={<CalendarMonthIcon sx={{ fontSize: 44 }} />}
              title="Sales & Demos"
              description="Get a personalised walkthrough and discuss custom plans for your team."
              detail="sales@[CONTACT EMAIL]"
              cta="Book a demo"
              onClick={() => window.open('mailto:sales@[CONTACT EMAIL]')}
            />
          </Grid>
        </Grid>

        <Grid container spacing={6} alignItems="flex-start">

          {/* ── Contact form ─────────────────────────────────────────────── */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                p: { xs: 3, sm: 5 },
                border: '1px solid rgba(0,180,216,0.12)',
                borderRadius: 3,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="h5" fontWeight={700} mb={0.5}>
                Send us a message
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={4}>
                We read every message and aim to respond within one business day.
              </Typography>

              {submitted ? (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  Thanks for reaching out! We'll be in touch within one business day.
                </Alert>
              ) : (
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Your name"
                        fullWidth required
                        value={form.name}
                        onChange={handleChange('name')}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email address"
                        type="email"
                        fullWidth required
                        value={form.email}
                        onChange={handleChange('email')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Company (optional)"
                        fullWidth
                        value={form.company}
                        onChange={handleChange('company')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Subject"
                        select
                        fullWidth
                        value={form.subject}
                        onChange={handleChange('subject')}
                      >
                        {SUBJECTS.map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Message"
                        multiline rows={5}
                        fullWidth required
                        placeholder="Tell us how we can help…"
                        value={form.message}
                        onChange={handleChange('message')}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth size="large"
                        disabled={submitting}
                        sx={{ fontWeight: 700, py: 1.5 }}
                      >
                        {submitting ? <CircularProgress size={22} color="inherit" /> : 'Send Message'}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Grid>

          {/* ── Office info ──────────────────────────────────────────────── */}
          <Grid item xs={12} md={5}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={700} mb={3} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" fontSize="small" /> Our office
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <LocationOnIcon sx={{ color: 'primary.main', mt: 0.25, flexShrink: 0 }} />
                <Box>
                  <Typography variant="body2" fontWeight={600}>[COMPANY NAME]</Typography>
                  <Typography variant="body2" color="text.secondary">[ADDRESS LINE 1]</Typography>
                  <Typography variant="body2" color="text.secondary">[ADDRESS LINE 2]</Typography>
                  <Typography variant="body2" color="text.secondary">[CITY, POSTCODE]</Typography>
                  <Typography variant="body2" color="text.secondary">[COUNTRY]</Typography>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Support hours</Typography>
              <Stack spacing={0.75}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Monday – Friday</Typography>
                  <Typography variant="body2">9:00 – 18:00 GMT</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Saturday – Sunday</Typography>
                  <Typography variant="body2">Emergency only</Typography>
                </Box>
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Response times</Typography>
              <Stack spacing={0.75}>
                {[
                  { type: 'General enquiries', time: '1 business day' },
                  { type: 'Technical support', time: '4 hours' },
                  { type: 'Security incidents', time: '1 hour' },
                ].map(({ type, time }) => (
                  <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{type}</Typography>
                    <Typography variant="body2" fontWeight={500}>{time}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <Box component="footer" sx={{ borderTop: '1px solid rgba(255,255,255,0.06)', py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.disabled">
          © {new Date().getFullYear()} [COMPANY NAME] · All rights reserved
        </Typography>
      </Box>
    </Box>
  );
}
